import { useMutation, useQuery } from "@apollo/client/react"
import { Star } from "lucide-react"
import React, { useMemo } from "react"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import { cn } from "~/common/cn"
import { useDocumentTitle } from "~/common/use-document-title"
import { GraphqlError } from "~/ui/graphql-error"

const WATCHLIST_QUERY = gql(/* GraphQL */ `
  query Watchlist {
    watchedListings {
      id
      listing {
        id
        title
        price
        photoUrl
        listingUrl
        endsAt
        location
        source
      }
    }
  }
`)

const WATCHED_LISTING_DELETE_MUTATION = gql(/* GraphQL */ `
  mutation WatchlistDelete($input: WatchedListingDeleteInput!) {
    watchedListingDelete(input: $input) {
      success
    }
  }
`)

function etaLabel(endsAt: string | null | undefined): string {
  if (!endsAt) return ""
  const diff = new Date(endsAt).getTime() - Date.now()
  if (diff <= 0) return "Ended"
  const h = Math.floor(diff / 3600000)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

function sourceBadgeLabel(source: string): string {
  if (source === "ctbids") return "CTBIDS"
  return "AUCTION NINJA"
}

function sourceBadgeColor(source: string): string {
  if (source === "ctbids") return "bg-emerald-800 text-stone-50"
  return "bg-stone-800 text-stone-50"
}

export const WatchlistScreen: React.FC = () => {
  useDocumentTitle("Watchlist")

  const { data, loading, error, refetch } = useQuery(WATCHLIST_QUERY)
  const [watchedListingDelete] = useMutation(WATCHED_LISTING_DELETE_MUTATION)

  const watchedListings = useMemo(() => data?.watchedListings ?? [], [data])

  const handleUnwatch = async (id: string) => {
    await watchedListingDelete({
      variables: { input: { id } },
      onError: (err) => toast.error(err.message),
      onCompleted: () => refetch(),
    })
  }

  if (error) return <GraphqlError error={error} />

  return (
    <div className="flex flex-1 flex-col min-h-0 bg-stone-50">
      <div className="px-6 py-5 border-b border-stone-200 bg-white">
        <h1
          className="text-sm font-bold text-stone-900"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Watchlist
        </h1>
        <p className="text-[11px] text-stone-400 mt-0.5">Active listings you&apos;re watching</p>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-4 sm:px-6 sm:py-5">
        {loading && <p className="text-sm text-stone-400">Loading…</p>}

        {!loading && watchedListings.length === 0 && (
          <div className="text-center py-16 border border-dashed border-stone-200">
            <Star size={24} className="mx-auto text-stone-300 mb-3" />
            <p className="text-stone-400 text-sm">No watched listings yet.</p>
            <p className="text-stone-400 text-sm mt-1">
              Star a listing from the dashboard to watch it here.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
          {watchedListings.map((wl) => {
            const listing = wl.listing
            return (
              <div key={wl.id} className="relative">
                <a
                  href={listing.listingUrl ?? "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-white border border-stone-200 hover:border-stone-400 transition-colors overflow-hidden"
                >
                  <div className="relative aspect-square bg-stone-100 overflow-hidden">
                    {listing.photoUrl ? (
                      <img
                        src={listing.photoUrl}
                        alt={listing.title ?? ""}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300 text-4xl">
                        ·
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleUnwatch(wl.id)
                      }}
                      className="absolute top-2 right-2 flex items-center justify-center w-7 h-7 bg-amber-600 text-white transition-colors hover:bg-amber-700"
                      aria-label="Remove from watchlist"
                    >
                      <Star size={13} fill="currentColor" />
                    </button>
                  </div>
                  <div className="p-2 sm:p-3 space-y-1">
                    {listing.title && (
                      <p className="text-xs text-stone-700 leading-snug line-clamp-2 font-medium">
                        {listing.title}
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span
                        className={cn(
                          "text-[9px] font-bold tracking-wide px-1.5 py-0.5",
                          sourceBadgeColor(listing.source)
                        )}
                      >
                        {sourceBadgeLabel(listing.source)}
                      </span>
                    </div>
                    {listing.price && (
                      <p className="text-sm font-bold text-amber-700">{listing.price}</p>
                    )}
                    <div className="flex items-center justify-between">
                      {listing.endsAt && (
                        <p className="text-[10px] text-stone-400 uppercase tracking-wide">
                          TIME LEFT {etaLabel(listing.endsAt)}
                        </p>
                      )}
                      {listing.location && (
                        <p className="text-[10px] text-stone-400 truncate max-w-[120px]">
                          {listing.location}
                        </p>
                      )}
                    </div>
                  </div>
                </a>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
